import { Box, Paper, Skeleton, Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import AppNav from '@sophocles/components/App/AppNav';
import TitleBar from '@sophocles/components/App/TitleBar';
import { useRetrieveOrderSaleQuery } from '@sophocles/features/entities/orderSales';
import OrderSaleForm from '@sophocles/views/sales/forms/OrderSaleForm';

function OrderSaleFormView() {
  const { orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Extract query parameters from the URL
  const queryParams = new URLSearchParams(location.search);
  const customerId = queryParams.get('customerId');
  const siteGroupId = queryParams.get('siteGroupId');

  // Duplicate-order prefill: passed via router state from OrderDetailDrawer.
  const duplicateFrom = location.state?.duplicateFrom ?? null;

  const { data: order, isLoading: loading } = useRetrieveOrderSaleQuery({ id: orderId }, { skip: orderId == null });

  return (
    <AppNav loading={loading} page={`sales_orders_order_form`} notFullHeight={true}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          overflow: 'hidden',
          padding: '0 var(--page-px) var(--page-py)',
        }}
      >
        <TitleBar showBackButton title={t('warehouse.order', 'Order')} />
        <Paper
          sx={{
            display: 'flex',
            flex: 1,
            overflow: 'auto',
            bgcolor: 'var(--card-bg)',
            border: '1px solid var(--card-border)',
            borderRadius: 'var(--card-radius)',
          }}
        >
          {loading ? (
            <Stack spacing={2} p={2} flex={1}>
              {/* Header */}
              <Skeleton variant="text" width="40%" height={30} />

              {/* Customer & Site Group Dropdowns */}
              <Box display="flex" gap={2}>
                <Skeleton variant="rectangular" height={40} width="50%" />
                <Skeleton variant="rectangular" height={40} width="50%" />
              </Box>

              {/* Order Summary Section */}
              <Skeleton variant="text" width="30%" height={25} />
              <Skeleton variant="rectangular" height={200} />

              {/* Shipping & Billing Sections */}
              <Box display="flex" gap={2}>
                <Skeleton variant="rectangular" height={150} width="50%" />
                <Skeleton variant="rectangular" height={150} width="50%" />
              </Box>

              {/* Table Header */}
              <Skeleton variant="text" width="100%" height={30} />

              {/* Table Row (for Totals) */}
              <Skeleton variant="rectangular" height={40} width="100%" />

              {/* Footer Buttons */}
              <Box display="flex" justifyContent="space-between">
                <Skeleton variant="rectangular" height={40} width={100} />
                <Skeleton variant="rectangular" height={40} width={80} />
              </Box>
            </Stack>
          ) : (
            <OrderSaleForm
              customerId={customerId}
              siteGroupId={siteGroupId}
              orderSale={order}
              duplicateFrom={duplicateFrom}
              onClose={id => {
                navigate(`/sales/orders/`);
              }}
            />
          )}
        </Paper>
      </Box>
    </AppNav>
  );
}

export default OrderSaleFormView;
